import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	output,
	signal,
	untracked,
	viewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { DEFAULT_THEME } from '@framework/analytics/defaults/theme.defaults';
import { ThemeSwitchComponent } from '@framework/analytics/components/styling/theme/jepto-theme-switch/theme-switch.component';
import {
	ThemeMode,
	ThemeService,
} from '@framework/analytics/services/theme.service';
import { JeptoButtonComponent } from '@framework/ui-lib/button/button.component';
import { JeptoUiInputComponent } from '@framework/ui-lib/form-controls/jepto-ui-input/jepto-ui-input.component';
import { JepInitialsPipe } from '@jepto/shared/pipes/jep-initials.pipe';
import { ViewerAuthService } from '../../services/viewer-auth.service';
import { ViewAccessService } from '../../services/view-access.service';

/** Which panel the gate shows. `denied` is also used for expired and disabled shares. */
export type ReportAccessGateStep =
	| 'email'
	| 'code'
	| 'request'
	| 'requested'
	| 'denied';

export type ReportAccessGateColorScheme = 'light' | 'dark' | 'system';

const GENERIC_DENIAL =
	'This report link is not available. Ask the report owner for a new link.';

const THEME_STORAGE_KEY = 'jepto_report_gate_theme';
const OTP_LENGTH = 6;
const MISSING_EMAIL_IN_LINK_MESSAGE =
	'This link is missing your email address. Use the full link from your email, or go back and enter your email.';

/**
 * PRIVATE share gate: email → emailed OTP → wrapper JWT.
 * Renders instead of the report until the viewer holds a session for this `reportId`.
 */
@Component({
	selector: 'analytics-report-access-gate',
	imports: [
		FormsModule,
		JeptoButtonComponent,
		JeptoUiInputComponent,
		JepInitialsPipe,
		ThemeSwitchComponent,
	],
	templateUrl: './report-access-gate.component.html',
	styleUrl: './report-access-gate.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		class: 'analytics-report-access-gate',
		'[class.analytics-report-access-gate--light]':
			'resolvedColorScheme() === "light"',
		'[class.analytics-report-access-gate--dark]':
			'resolvedColorScheme() === "dark"',
	},
})
export class ReportAccessGateComponent {
	private readonly viewerAuth = inject(ViewerAuthService);
	private readonly viewAccess = inject(ViewAccessService);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly themeService = inject(ThemeService);

	readonly testId = input<string | undefined>();
	readonly reportId = input.required<string>();
	readonly initialStep = input<ReportAccessGateStep>('email');
	readonly brandName = input<string | undefined>();
	readonly brandLogoUrl = input<string | null | undefined>();
	readonly showThemeSwitcher = input(true);
	readonly initialColorScheme = input<ReportAccessGateColorScheme | undefined>();
	readonly initialEmail = input<string | undefined>();
	readonly initialOtp = input<string | undefined>();

	readonly verified = output<void>();

	readonly otpLength = OTP_LENGTH;

	readonly step = signal<ReportAccessGateStep>('email');
	readonly email = signal('');
	readonly requestNote = signal('');
	readonly code = signal('');
	readonly busy = signal(false);
	readonly errorMessage = signal<string | null>(null);
	readonly genericDenial = GENERIC_DENIAL;

	readonly resolvedColorScheme =
		this.themeService.getActiveThemeModeSignal();

	/** True once `sendViewShareCode` returned `CODE_SENT` for this attempt. */
	private codeSendStarted = false;

	readonly otpInputs = viewChildren<ElementRef<HTMLInputElement>>('otpCell');
	readonly focusedOtpIndex = signal<number | null>(null);

	readonly otpDigits = computed(() => {
		const chars = this.code()
			.replace(/\D/g, '')
			.slice(0, OTP_LENGTH)
			.split('');
		while (chars.length < OTP_LENGTH) {
			chars.push('');
		}
		return chars;
	});

	/** Tracks whether OTP focus ran for the current visit to the code step. */
	private otpFocusReady = false;

	constructor() {
		queueMicrotask(() => {
			this.#initializeGate();
		});

		effect(() => {
			const step = this.step();
			if (step !== 'code') {
				this.otpFocusReady = false;
				return;
			}
			if (this.busy() || this.otpFocusReady) {
				return;
			}
			this.otpFocusReady = true;
			untracked(() => {
				queueMicrotask(() => this.#focusOtpInput(0));
			});
		});
	}

	async onSendCode(): Promise<void> {
		if (this.busy()) {
			return;
		}
		const email = this.email().trim().toLowerCase();
		if (!email) {
			this.errorMessage.set('Enter your email address.');
			return;
		}

		this.busy.set(true);
		this.errorMessage.set(null);
		try {
			const outcome = await this.viewAccess.sendViewShareCode(
				this.reportId(),
				email
			);
			if (outcome.status === 'ACCESS_REQUIRED') {
				this.#goToRequestAccessStep();
				return;
			}
			if (outcome.status !== 'CODE_SENT') {
				this.errorMessage.set(
					'We could not send a code to that address.'
				);
				return;
			}
			this.codeSendStarted = true;
			this.code.set('');
			this.step.set('code');
		} finally {
			this.busy.set(false);
		}
	}

	async onVerifyCode(): Promise<void> {
		if (this.busy()) {
			return;
		}
		if (!this.codeSendStarted) {
			this.step.set('email');
			return;
		}
		const email = this.email().trim().toLowerCase();
		if (!email) {
			this.errorMessage.set(MISSING_EMAIL_IN_LINK_MESSAGE);
			return;
		}
		const code = this.code().trim();
		if (code.length < OTP_LENGTH) {
			this.errorMessage.set('Enter the 6-digit code from your email.');
			return;
		}

		this.busy.set(true);
		this.errorMessage.set(null);
		try {
			const reportId = this.reportId();
			const verifyOutcome = await this.viewAccess.verifyViewShareCode(
				reportId,
				email,
				code
			);
			if (verifyOutcome.status === 'ok') {
				this.viewerAuth.storeViewerSession(
					email,
					reportId,
					verifyOutcome.token
				);
				this.verified.emit();
				return;
			}
			if (verifyOutcome.status === 'invalid') {
				this.errorMessage.set('That code was not correct.');
				return;
			}
			if (verifyOutcome.status === 'access_required') {
				this.#goToRequestAccessStep();
				return;
			}
			this.errorMessage.set(
				verifyOutcome.status === 'unavailable'
					? 'Use the link in your email to open this report, or request a new code.'
					: 'That code was not correct.'
			);
		} catch (error) {
			this.errorMessage.set(
				error instanceof Error && error.message
					? error.message
					: 'That code was not correct.'
			);
		} finally {
			this.busy.set(false);
		}
	}

	onAlreadyHaveCode(): void {
		if (this.busy()) {
			return;
		}
		this.errorMessage.set(null);
		if (!this.email().trim()) {
			this.errorMessage.set('Enter your email address first.');
			return;
		}
		this.codeSendStarted = true;
		this.code.set('');
		this.focusedOtpIndex.set(null);
		this.step.set('code');
	}

	async onResendCode(): Promise<void> {
		if (this.busy()) {
			return;
		}
		this.codeSendStarted = false;
		this.code.set('');
		this.focusedOtpIndex.set(null);
		await this.onSendCode();
	}

	async onRequestAccess(): Promise<void> {
		if (this.busy()) {
			return;
		}
		const email = this.email().trim().toLowerCase();
		if (!email) {
			this.errorMessage.set('Enter your email address first.');
			return;
		}

		this.busy.set(true);
		this.errorMessage.set(null);
		try {
			const ok = await this.viewAccess.createViewAccessRequest(
				this.reportId(),
				email,
				this.requestNote()
			);
			if (!ok) {
				this.errorMessage.set(
					'We could not send your request. Try again.'
				);
				return;
			}
			this.step.set('requested');
		} finally {
			this.busy.set(false);
		}
	}

	onStartOver(): void {
		if (this.busy()) {
			return;
		}
		this.codeSendStarted = false;
		this.code.set('');
		this.requestNote.set('');
		this.focusedOtpIndex.set(null);
		this.errorMessage.set(null);
		this.step.set('email');
	}

	onOtpFocus(index: number): void {
		this.focusedOtpIndex.set(index);
	}

	onOtpBlur(): void {
		this.focusedOtpIndex.set(null);
	}

	onOtpInput(index: number, event: Event): void {
		if (this.busy()) {
			return;
		}
		const input = event.target as HTMLInputElement;
		const raw = input.value.replace(/\D/g, '');
		if (raw.length > 1) {
			this.#applyOtpString(raw);
			return;
		}
		const digits = [...this.otpDigits()];
		digits[index] = raw.slice(-1);
		const next = digits.join('').slice(0, OTP_LENGTH);
		this.code.set(next);
		this.errorMessage.set(null);
		if (raw && index < OTP_LENGTH - 1) {
			this.#focusOtpInput(index + 1);
		}
		if (next.length === OTP_LENGTH) {
			void this.onVerifyCode();
		}
	}

	onOtpKeydown(index: number, event: KeyboardEvent): void {
		if (this.busy()) {
			return;
		}
		if (event.key === 'Backspace' && !this.otpDigits()[index] && index > 0) {
			event.preventDefault();
			const digits = [...this.otpDigits()];
			digits[index - 1] = '';
			this.code.set(digits.join('').trim());
			this.#focusOtpInput(index - 1);
		}
	}

	onOtpPaste(event: ClipboardEvent): void {
		if (this.busy()) {
			return;
		}
		event.preventDefault();
		const pasted = event.clipboardData?.getData('text') ?? '';
		this.#applyOtpString(pasted);
	}

	isOtpCellActive(index: number): boolean {
		return (
			Boolean(this.otpDigits()[index]) || this.focusedOtpIndex() === index
		);
	}

	#applyOtpString(value: string): void {
		const next = value.replace(/\D/g, '').slice(0, OTP_LENGTH);
		this.code.set(next);
		this.errorMessage.set(null);
		if (next.length === OTP_LENGTH) {
			void this.onVerifyCode();
			return;
		}
		this.#focusOtpInput(next.length);
	}

	#focusOtpInput(index: number): void {
		const inputs = this.otpInputs();
		inputs[index]?.nativeElement.focus();
	}

	#initializeGate(): void {
		this.step.set(this.initialStep());
		this.#initializeThemePreference();
		const email = this.initialEmail()?.trim().toLowerCase();
		if (email) {
			this.email.set(email);
		}
		if (this.initialStep() === 'code') {
			this.codeSendStarted = true;
		}

		const storyOtp = this.#normalizeOtp(this.initialOtp() ?? null);
		if (storyOtp) {
			this.#startCodeEntryWithOtp(storyOtp);
		}

		const queryCode = this.#readCodeFromQuery();
		if (queryCode) {
			const queryEmail = this.#readEmailFromQuery();
			if (queryEmail) {
				this.email.set(queryEmail);
			}
			void this.#stripInboundQueryParams();
			if (!queryEmail) {
				this.#startCodeEntryWithOtp(queryCode);
				this.errorMessage.set(MISSING_EMAIL_IN_LINK_MESSAGE);
			} else {
				void this.#handleInboundShareLink(queryCode, queryEmail);
			}
		}
	}

	async #handleInboundShareLink(code: string, email: string): Promise<void> {
		this.code.set(code);
		this.codeSendStarted = true;
		this.step.set('code');
		this.otpFocusReady = true;
		await this.onVerifyCode();
	}

	#goToRequestAccessStep(): void {
		this.requestNote.set('');
		this.codeSendStarted = false;
		this.code.set('');
		this.focusedOtpIndex.set(null);
		this.errorMessage.set(null);
		this.step.set('request');
	}

	#startCodeEntryWithOtp(otp: string): void {
		this.code.set(otp);
		this.codeSendStarted = true;
		this.step.set('code');
		this.errorMessage.set(null);
		this.otpFocusReady = true;
	}

	#readCodeFromQuery(): string | null {
		const params = this.route.snapshot.queryParamMap;
		return this.#normalizeOtp(params.get('code'));
	}

	#readEmailFromQuery(): string | null {
		const email = this.route.snapshot.queryParamMap
			.get('email')
			?.trim()
			.toLowerCase();
		return email || null;
	}

	#normalizeOtp(raw: string | null): string | null {
		if (!raw) {
			return null;
		}
		const digits = raw.replace(/\D/g, '');
		return digits.length === OTP_LENGTH ? digits : null;
	}

	async #stripInboundQueryParams(): Promise<void> {
		const params = this.route.snapshot.queryParamMap;
		if (!params.has('code') && !params.has('email')) {
			return;
		}
		await this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { code: null, email: null },
			queryParamsHandling: 'merge',
			replaceUrl: true,
		});
	}

	#initializeThemePreference(): void {
		this.themeService.loadThemeFromConfig(DEFAULT_THEME);

		const scheme =
			this.initialColorScheme() ?? this.#readLegacyGateThemePreference();
		if (scheme) {
			this.themeService.setThemeMode(this.#toThemeMode(scheme));
			return;
		}

		this.themeService.initFromViewConfig('auto');
	}

	#readLegacyGateThemePreference(): ReportAccessGateColorScheme | null {
		if (typeof localStorage === 'undefined') {
			return null;
		}
		const stored = localStorage.getItem(THEME_STORAGE_KEY);
		if (stored === 'light' || stored === 'dark' || stored === 'system') {
			localStorage.removeItem(THEME_STORAGE_KEY);
			return stored;
		}
		return null;
	}

	#toThemeMode(scheme: ReportAccessGateColorScheme): ThemeMode {
		return scheme === 'system' ? 'auto' : scheme;
	}
}
